import React, { useEffect, useRef, useState } from 'react';

import { mapApi } from '../../requests/api';
import { ownerResearchApi } from '../../requests/ownerResearch';
import { formatStoredPhoneNumber } from '../../utils';
import { dropRestOfDoubleClick } from '../../utils/dropRestOfDoubleClick';
import { getOpenInCrmHref } from '../OpenInCrmButton/OpenInCrmButton';
import { CheckIcon, noteOf, REVIEW_KIND_LABEL } from './vocabulary';

// The map's Kore window words, so the client reads the same thing in both places.
const STRENGTH_LABEL = { definite: 'Likely the same', possible: 'Possible' };

const MATCHED_LABEL = {
    email: 'Same email',
    cell: 'Same cell',
    phone: 'Same phone',
    name: 'Same name',
    nickname: 'Nickname',
    company: 'Same company',
    city: 'Same city',
    address: 'Same address',
    lightbox: 'Filed from this LightBox person',
};

// Shown with the value: the shared number or address is the evidence.
const MATCHED_WITH_VALUE = ['email', 'cell', 'phone', 'address', 'nickname', 'city'];

const NUMBER_FIELDS = ['cell', 'phone'];

// How far a record iCLIMB holds goes, as the server judged it.
const SAME_PERSON = 'definite';
const POSSIBLE = 'possible';

// The provider scores 1 (best) to 5; the card's meter fills five bars for a 1.
const METER_BARS = 5;

// The client's order and names: whoever is already in iCLIMB or Kore first, the bot's person filed new second.
const OPTION_ICLIMB = 'Option 1 · iCLIMB Search';
const OPTION_BOT = 'Option 2 · BOT Research';
const CURRENT_INVESTOR = 'Current Investor in iCLIMB';
const LEAVE_AS_IS = 'Leave As Is';

// The server's own text for this is a save's ("nothing was saved"); here only a check failed.
const KORE_UNAVAILABLE = 'KORE_UNAVAILABLE';
const KORE_UNREACHED = "Couldn't reach Kore to check this person.";

// Under the bot's heading when Option 1 shows someone of the same name: the client added Ron
// Berlin again from here, a duplicate, not seeing why the same name sat in both places.
const SAME_NAME = 'name';
const TWICE_TITLE = 'Why twice?';
const TWICE_WHY = 'The bot almost added this person as new, but they may be the one in Option 1.';
const TWICE_MAYBE = 'This may be the person in Option 1.';
const TWICE_SAME = 'If so, pick them there. Adding here makes a duplicate.';

const PHONE_TYPE_LABEL = { C: 'Cell', L: 'Phone' };

function initials(name) {
    return String(name || '')
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
}

function titleCase(text) {
    return String(text || '').toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function nameOf(row) {
    return row.name || (row.business && row.business.name) || 'Unnamed contact';
}

function roleOf(row) {
    return [row.titles && row.titles.length ? row.titles.map(titleCase).join(', ') : null, row.business && row.business.name]
        .filter(Boolean)
        .join(' · ');
}

function placeOf(address) {
    if (!address) return null;
    return [address.street, address.city, [address.state, address.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null;
}

// The provider marks a verified email with a trailing "(v)"; the mark is not part of the address.
function cleanEmail(email) {
    return String(email || '').replace(/\s*\(v\)\s*$/i, '').trim();
}

// How to reach the LightBox person: every number with its kind, every email.
function contactOf(row) {
    const items = [];
    (row.phones || []).forEach((phone) => {
        if (phone.number) items.push({ label: PHONE_TYPE_LABEL[phone.type] || 'Phone', value: formatStoredPhoneNumber(phone.number) });
    });
    (row.emails || []).forEach((email) => {
        const clean = cleanEmail(email);
        if (clean) items.push({ label: 'Email', value: clean });
    });
    return items;
}

// How to reach an investor iCLIMB holds, as its record has it.
function contactOfInvestor(investor) {
    return [
        investor.cell && { label: 'Cell', value: formatStoredPhoneNumber(investor.cell) },
        investor.phone && { label: 'Phone', value: formatStoredPhoneNumber(investor.phone) },
        investor.email && { label: 'Email', value: investor.email },
    ].filter(Boolean);
}

function ContactLine({ items }) {
    if (!items.length) return null;

    return (
        <div className="or-contact">
            {items.map((item, index) => (
                <span key={index} className="or-contact__item">
                    <span className="or-contact__label">{item.label}</span>
                    <span className="or-contact__value">{item.value}</span>
                </span>
            ))}
        </div>
    );
}

function Meter({ confidence }) {
    if (confidence === null || confidence === undefined) return null;

    const strength = Math.max(1, Math.min(METER_BARS, METER_BARS + 1 - confidence));

    return (
        <span className="or-meter" title={'Confidence ' + confidence + ' (1 is best)'}>
            {Array.apply(null, { length: METER_BARS }).map((ignored, index) => (
                <span key={index} className={'or-meter__bar' + (index < strength ? ' is-on' : '')} />
            ))}
        </span>
    );
}

function ParcelBlock({ parcel, found }) {
    return (
        <div className="or-block">
            <div className="or-label">Parcel</div>
            <div className="or-parcel">
                <div className="or-parcel__address">{parcel.address}</div>
                <div className="or-parcel__checks">
                    {found.indexOf('Address') !== -1 && <span className="or-tick"><CheckIcon />Address</span>}
                    {found.toLowerCase().indexOf('pin') !== -1 && <span className="or-tick"><CheckIcon />Map pin</span>}
                </div>
            </div>
        </div>
    );
}

// How to reach the person at a glance: the number and address a save would take, and where they are.
function reachOf(row) {
    return [row.default_cell || row.default_phone ? formatStoredPhoneNumber(row.default_cell || row.default_phone) : null, cleanEmail(row.default_email)]
        .filter(Boolean)
        .join(' · ');
}

function whereOf(row) {
    const address = row.address || (row.business && row.business.address);
    return address ? [address.city, address.state].filter(Boolean).join(', ') : '';
}

// Every record iCLIMB holds that may be this person, likeliest first.
function onFileOf(row) {
    return (row && row.on_file) || [];
}

// The record is them, or carries their name or a nickname of it — not another person sharing an email.
function isThem(record) {
    return record.strength === SAME_PERSON
        || record.matched.some((each) => each.field === 'name' || each.field === 'nickname');
}

function OpenInKore({ leadId }) {
    return (
        <a className="ls-linkbtn or-korelink" href={getOpenInCrmHref(leadId, 'lead')} target="_blank" rel="noopener noreferrer">
            Open in Kore
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                <path d="M3.6 7.4 L7.4 3.6 M4.2 3.6 H7.4 V6.8" />
            </svg>
        </a>
    );
}

// A record iCLIMB holds, with why it may be this person; another name says it is only possible.
function RecordCard({ record, disabled, onUse }) {
    const sub = [record.company, record.address ? [record.address.city, record.address.state].filter(Boolean).join(', ') : null]
        .filter(Boolean)
        .join(' · ');

    return (
        <div className="or-option or-option--held">
            <div className="or-avatar" aria-hidden="true">{initials(record.name)}</div>
            <div>
                <div className="or-option__namerow">
                    <a className="or-option__name" href={'/investor/' + record.id} target="_blank" rel="noopener noreferrer">{record.name}</a>
                    {!isThem(record) && <span className="or-pill or-pill--possible">{STRENGTH_LABEL[POSSIBLE]}</span>}
                    {record.deceased && <span className="or-pill or-pill--quiet">Deceased</span>}
                </div>
                {sub && <div className="or-option__meta">{sub}</div>}
                <ContactLine items={contactOfInvestor(record)} />
                <Evidence matched={record.matched} />
            </div>
            <div className="or-option__actions">
                <button type="button" className="ls-btn ls-btn--small" disabled={disabled} onClick={onUse}>Use this investor</button>
                {record.lead_id && <OpenInKore leadId={record.lead_id} />}
            </div>
        </div>
    );
}

// The run's own pick is the "Most likely" here; the card's pill means the best provider score.
function People({ rows, selectedKey, pickedKey, notOwners, onPick }) {
    return (
        <div className="or-block">
            <div className="or-label">API · {rows.length}</div>
            <div className="or-people">
                {rows.map((row) => {
                    const out = notOwners.indexOf(row.key) !== -1;
                    const body = (
                        <React.Fragment>
                            <span className="or-row__radio" />
                            <span className="or-row__body">
                                <span className="or-row__top">
                                    <span className="or-row__name">{nameOf(row)}</span>
                                    {!out && <Meter confidence={row.confidence} />}
                                </span>
                                <span className="or-row__sub">{roleOf(row)}</span>
                                {!out && reachOf(row) && <span className="or-row__sub or-row__reach">{reachOf(row)}</span>}
                                {!out && whereOf(row) && <span className="or-row__sub">{whereOf(row)}</span>}
                                <span className="or-row__pills">
                                    {out && <span className="or-pill or-pill--quiet">Not an owner</span>}
                                    {!out && row.matches_assessor && <span className="or-pill or-pill--entity">Same entity</span>}
                                    {!out && row.key === pickedKey && <span className="or-pill or-pill--likely">Most likely</span>}
                                    {!out && onFileOf(row).length > 0 && (onFileOf(row).some(isThem)
                                        ? <span className="or-pill or-pill--quiet">In iCLIMB</span>
                                        : <span className="or-pill or-pill--possible">Possible match</span>)}
                                </span>
                            </span>
                        </React.Fragment>
                    );

                    return out ? (
                        <div key={row.key} className="or-row is-out">{body}</div>
                    ) : (
                        <button
                            key={row.key}
                            type="button"
                            className={'or-row' + (row.key === selectedKey ? ' is-on' : '')}
                            onClick={() => onPick(row.key)}
                        >
                            {body}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// What two records share, each detail ticked: the evidence a person judges a match on.
function Evidence({ matched }) {
    if (!matched || !matched.length) return null;

    return (
        <div className="or-evidence">
            {matched.map((each) => (
                <span key={each.field} className="or-ev">
                    <CheckIcon size={13} />
                    {MATCHED_LABEL[each.field] || each.field}
                    {MATCHED_WITH_VALUE.indexOf(each.field) !== -1 && each.value && <b>{NUMBER_FIELDS.indexOf(each.field) !== -1 ? formatStoredPhoneNumber(each.value) : each.value}</b>}
                </span>
            ))}
        </div>
    );
}

function MatchCard({ match, disabled, onUse }) {
    const sub = [
        match.company,
        [match.city, match.state].filter(Boolean).join(', '),
        match.lead_owner && 'Lead owner ' + match.lead_owner,
    ].filter(Boolean).join(' · ');
    const contact = [
        match.phone && { label: 'Phone', value: formatStoredPhoneNumber(match.phone) },
        match.email && { label: 'Email', value: match.email },
    ].filter(Boolean);

    return (
        <div className="or-option">
            <div className="or-avatar" aria-hidden="true">{initials(match.name)}</div>
            <div>
                <div className="or-option__namerow">
                    <span className="or-option__name">{match.name || 'Unnamed lead'}</span>
                    <span className={'or-pill ' + (match.strength === 'definite' ? 'or-pill--likely' : 'or-pill--possible')}>
                        {STRENGTH_LABEL[match.strength] || match.label}
                    </span>
                    <span className={'or-pill ' + (match.investor_id ? 'or-pill--entity' : 'or-pill--quiet')}>
                        {match.investor_id ? 'In iCLIMB' : 'Kore only'}
                    </span>
                </div>
                {sub && <div className="or-option__meta">{sub}</div>}
                <ContactLine items={contact} />
                <Evidence matched={match.matched} />
            </div>
            <div className="or-option__actions">
                <button type="button" className="ls-btn ls-btn--small" disabled={disabled} onClick={onUse}>Use this investor</button>
                <OpenInKore leadId={match.lead_id} />
            </div>
        </div>
    );
}

/**
 * One property a run left for a person. What LightBox gave on the left, as the
 * parcel card shows it; the decision on the right as options, in the client's
 * order: every record iCLIMB or Kore holds that may be them, then the bot's pick filed new.
 * Saving goes through the card's own endpoint, so the owner lands exactly as
 * it would from the map.
 */
export default function ReviewWindow({ item, onClose, onResolved }) {
    const [detail, setDetail] = useState(null);
    const [rows, setRows] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const [check, setCheck] = useState(null);
    const [saving, setSaving] = useState(false);
    const [failure, setFailure] = useState(null);
    // Beside an owner on file the new person joins by default; replacing is the deliberate choice.
    const [alongside, setAlongside] = useState(true);
    const unmounted = useRef(false);
    // Kore's answer for each person asked about while the window is open, so going back to one does not ask again.
    const asked = useRef({});
    // Bumped by Try again, so the person picked is asked about once more.
    const [attempt, setAttempt] = useState(0);

    // A record on file is searched by its own details too (check_held), which
    // costs nothing for a person without one, so every ask is the same ask.
    const askKore = (parcel, key) => {
        if (asked.current[key]) return asked.current[key];

        const promise = mapApi.leadMatches([{ parcel, true_owner_key: key, check_held: true }]).then((results) => results[0]);
        asked.current[key] = promise;
        promise.catch(() => {
            if (asked.current[key] === promise) delete asked.current[key];
        });
        return promise;
    };

    const property = item.property || {};
    // Read from the property as it is now: an owner can arrive after the run, whatever the review's kind.
    const owners = (detail && detail.owners) || [];
    const hasOwner = owners.length > 0;

    useEffect(() => () => {
        unmounted.current = true;
    }, []);

    useEffect(() => {
        const onKeyDown = (event) => event.key === 'Escape' && !saving && onClose();
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose, saving]);

    // One request brings the parcel and everyone on it. Kore is asked about the
    // run's pick at the same moment, from the run's own notes, since it is the
    // slower of the two; the answer waits in the memo for the list to arrive.
    useEffect(() => {
        // The review first: a server that takes one request at a time still shows the people before Kore answers.
        ownerResearchApi.review(item.id).then((data) => {
            if (unmounted.current) return;
            setDetail(data);
            setRows(data.parcel ? data.people || [] : null);
            setSelectedKey(data.picked_key);
        }, (error) => !unmounted.current && setFailure(error.message));

        const parcelNote = noteOf(item, 'parcel');
        const ownerNote = noteOf(item, 'owner');
        const assessorNote = noteOf(item, 'assessor');

        if (parcelNote && parcelNote.value && ownerNote && ownerNote.value) {
            askKore({ parcel_id: parcelNote.value, owner_name: assessorNote ? assessorNote.value : null }, ownerNote.value).catch(() => null);
        }
    }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const selected = rows && rows.filter((row) => row.key === selectedKey)[0];
    const onFile = onFileOf(selected);

    // Kore is asked about whoever is picked, since filing them new is always on
    // offer. Kore not answering is the block's own state, with a way to ask again.
    useEffect(() => {
        if (!detail || !detail.parcel || !selected) return undefined;

        // An answer for someone picked before is dropped: it would sit under the wrong person.
        let current = true;
        setCheck(null);

        askKore(detail.parcel, selected.key).then(
            (result) => current && !unmounted.current && setCheck(result),
            (error) => current && !unmounted.current && setCheck({
                failed: error.code === KORE_UNAVAILABLE ? KORE_UNREACHED : error.message,
                matches: [],
                total: 0,
            }),
        );

        return () => {
            current = false;
        };
    }, [detail, selected, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

    const askKoreAgain = () => {
        delete asked.current[selected.key];
        setAttempt((count) => count + 1);
    };

    const finish = (request, resolution, messageOf) => {
        dropRestOfDoubleClick();
        setSaving(true);
        setFailure(null);
        let saved = null;
        request()
            .then((result) => {
                saved = result;
                return ownerResearchApi.resolve(item.id, resolution);
            })
            .then(
                () => !unmounted.current && onResolved(messageOf(saved)),
                (error) => {
                    if (unmounted.current) return;
                    setSaving(false);
                    setFailure(error.message);
                }
            );
    };

    // Named as saved: a lead or an iCLIMB record can carry another name than LightBox's.
    const savedMessage = (fallback) => (result) => ((result && result.investor && result.investor.name) || fallback)
        + (hasOwner && alongside ? ' is now an owner of ' : ' is now the owner of ') + property.address + '.';

    // The LightBox person, filed new or as the Kore lead picked. New even when
    // iCLIMB holds a record for them: those records are the first option.
    const saveOwner = (koreLeadId) => finish(
        () => mapApi.setPropertyOwner(property.id, {
            parcelId: detail.parcel.parcel_id,
            ownerName: detail.assessor,
            trueOwnerKey: selected.key,
            koreLeadId: koreLeadId || undefined,
            createNew: !koreLeadId,
            alongside: hasOwner && alongside,
        }),
        'owner',
        savedMessage(nameOf(selected)),
    );

    // A record iCLIMB holds, attached as any investor is; the note still comes off the parcel.
    const useRecord = (record) => finish(
        () => mapApi.setPropertyOwner(property.id, {
            parcelId: detail.parcel.parcel_id,
            ownerName: detail.assessor,
            investorId: record.id,
            alongside: hasOwner && alongside,
        }),
        'owner',
        savedMessage(record.name),
    );

    const leave = () => finish(() => Promise.resolve(), 'left', () => property.address + ' was left as Need Owner.');

    const keep = () => finish(() => Promise.resolve(), 'kept', () => property.address + ' keeps its owner on file.');

    const ready = Boolean(detail) && !saving;

    const place = [property.city, [property.state, property.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const parcelNote = detail && detail.review ? noteOf(detail.review, 'parcel') : null;
    const aiNote = noteOf(item, 'ai');
    const hasPeople = Boolean(detail && detail.parcel);
    // A Kore lead iCLIMB already lists above is the same record, so it is not shown twice.
    const listedIds = onFile.map((record) => String(record.id));
    const listedLeads = onFile.map((record) => String(record.lead_id || ''));
    const matches = check
        ? check.matches.filter((match) => listedIds.indexOf(String(match.investor_id || '')) === -1
            && listedLeads.indexOf(String(match.lead_id)) === -1)
        : [];
    const koreFailed = Boolean(check && check.failed);
    const found = onFile.length + matches.length;
    // Only the person the bot itself settled on is one it was about to add.
    const botsPick = Boolean(selected && detail && selected.key === detail.picked_key);
    const sameNameAbove = onFile.concat(matches).some((entry) => (entry.matched || []).some((each) => each.field === SAME_NAME));

    return (
        <div className="ls-modal">
            <div className="ls-modal__scrim" onClick={() => !saving && onClose()} />

            <div className={'ls-modal__dialog ' + (hasPeople || !detail ? 'or-dialog--split' : 'or-dialog--parcel')} role="dialog" aria-modal="true" aria-label={property.address}>
                <div className="ls-modal__head">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="ls-modal__title">{property.address}</div>
                        <div className="or-dialog__sub">{place}</div>
                    </div>
                    <button type="button" className="ls-modal__close" aria-label="Close" disabled={saving} onClick={onClose}>×</button>
                </div>

                <div className="ls-modal__body">
                    {failure && (
                        <div className="ls-modal__alert">
                            <span className="ls-alert__mark">!</span>
                            {failure}
                        </div>
                    )}

                    {!detail && !failure && <div className="or-loading">Loading…</div>}

                    {detail && !hasPeople && (
                        <div className="or-single">
                            <div className="or-label">{REVIEW_KIND_LABEL[item.kind] || 'Parcel'}</div>
                            <div className="or-parcel">
                                <div className="or-parcel__reason">{item.reason}</div>
                            </div>
                        </div>
                    )}

                    {hasPeople && (
                        <div className="or-split">
                            <div className="or-side">
                                <ParcelBlock parcel={detail.parcel} found={parcelNote ? parcelNote.text : ''} />
                                {rows && (
                                    <People
                                        rows={rows}
                                        selectedKey={selectedKey}
                                        pickedKey={detail.picked_key}
                                        notOwners={detail.not_owner_keys || []}
                                        onPick={(key) => !saving && setSelectedKey(key)}
                                    />
                                )}
                            </div>

                            <div className="or-main">
                                {aiNote && (
                                    <div className="or-block or-ai">
                                        <div className="or-label">AI</div>
                                        <div className="or-ai__text">{aiNote.text}</div>
                                    </div>
                                )}

                                {hasOwner && (
                                    <div className="or-block">
                                        <div className="or-label">{CURRENT_INVESTOR}</div>
                                        {owners.map((owner) => (
                                            <div key={owner.id} className="or-option">
                                                <div className="or-avatar" aria-hidden="true">{initials(owner.name)}</div>
                                                <div>
                                                    <a className="or-option__name" href={'/investor/' + owner.id} target="_blank" rel="noopener noreferrer">{owner.name}</a>
                                                    {owner.company && <div className="or-option__meta">{owner.company}</div>}
                                                    {placeOf(owner.address) && <div className="or-option__meta">{placeOf(owner.address)}</div>}
                                                    <ContactLine items={contactOfInvestor(owner)} />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="ls-chips or-choice">
                                            <button type="button" className={'ls-toggle' + (alongside ? ' is-on' : '')} disabled={saving} onClick={() => setAlongside(true)}>
                                                Add alongside
                                            </button>
                                            <button type="button" className={'ls-toggle' + (!alongside ? ' is-on' : '')} disabled={saving} onClick={() => setAlongside(false)}>
                                                Replace
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!rows && <div className="or-loading">Loading…</div>}

                                {rows && !selected && (
                                    <div className="or-block">
                                        <div className="or-label">{OPTION_ICLIMB}</div>
                                        <div className="or-nomatch">{rows.length ? 'Pick a person on the left.' : 'Nobody is listed for this parcel any more.'}</div>
                                    </div>
                                )}

                                {selected && (
                                    <div className="or-block">
                                        <div className="or-label">
                                            {OPTION_ICLIMB}
                                            {found > 0 && <span className="or-count">{found}</span>}
                                            {!check && ' · checking Kore…'}
                                        </div>
                                        {onFile.map((record) => (
                                            <RecordCard key={record.id} record={record} disabled={saving} onUse={() => useRecord(record)} />
                                        ))}
                                        {matches.map((match) => (
                                            <MatchCard key={match.lead_id} match={match} disabled={saving} onUse={() => saveOwner(match.lead_id)} />
                                        ))}
                                        {koreFailed && (
                                            <div className="or-nomatch or-korefailed">
                                                <span className="or-korefailed__text">{check.failed}</span>
                                                <button type="button" className="ls-btn ls-btn--small" disabled={saving} onClick={askKoreAgain}>Try again</button>
                                            </div>
                                        )}
                                        {check && !koreFailed && found === 0 && <div className="or-nomatch">Nobody in iCLIMB or Kore matches this person.</div>}
                                    </div>
                                )}

                                {selected && (
                                    <div className="or-block">
                                        <div className="or-label">{OPTION_BOT}</div>
                                        {sameNameAbove && (
                                            <p className="or-twice">
                                                <svg className="or-twice__icon" width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
                                                    <circle cx="6.5" cy="6.5" r="5.6" />
                                                    <path d="M6.5 5.8 V9.4 M6.5 3.7 V3.9" />
                                                </svg>
                                                <span>
                                                    {botsPick && <strong>{TWICE_TITLE} </strong>}
                                                    {(botsPick ? TWICE_WHY : TWICE_MAYBE) + ' ' + TWICE_SAME}
                                                </span>
                                            </p>
                                        )}
                                        <div className="or-option or-option--owner">
                                            <div className="or-avatar" aria-hidden="true">{initials(nameOf(selected))}</div>
                                            <div>
                                                <div className="or-option__name">{nameOf(selected)}</div>
                                                {roleOf(selected) && <div className="or-option__meta">{roleOf(selected)}</div>}
                                                {placeOf(selected.address) && <div className="or-option__meta">{placeOf(selected.address)}</div>}
                                                <ContactLine items={contactOf(selected)} />
                                            </div>
                                            <div className="or-option__actions">
                                                <button type="button" className="ls-btn ls-btn--small" disabled={saving || !check || koreFailed} onClick={() => saveOwner(null)}>
                                                    + Add as new investor
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="ls-modal__foot">
                    <a className="ls-btn ls-btn--small" href={'/map/parcels?property=' + property.id} target="_blank" rel="noopener noreferrer">Open on map</a>
                    <span className="ls-modal__hint" />
                    {hasOwner && <button type="button" className="ls-discard" disabled={!ready} onClick={keep}>{LEAVE_AS_IS}</button>}
                    <button type="button" className="ls-discard" disabled={!ready} onClick={leave}>Leave as Need Owner</button>
                </div>
            </div>
        </div>
    );
}
